import { GridProvider } from "@/types/LCAoM/GridProvider";
import { LCAoMService, NodeMaterialLookup } from "@/types/LCAoM/LCAoMService";
import { ModelsDataService } from "@/types/LCAoM/ModelsDataService";
import { materialsArrayToMap, readModelData, serializeMap, writeModelData } from "@/types/storage/modelDataStorage";
import { Co2MaterialType, NodeSupplementalDto } from "@api/apiClient";
import { useCo2MaterialTypesByIds, normalizeCo2MaterialTypeId } from "@api/co2MaterialTypes";
import { useAssignedCo2MaterialTypeIds } from "./useAssignedCo2MaterialTypeIds";
import { useCachedAssignedCo2MaterialTypeIds } from "./useCachedAssignedCo2MaterialTypeIds";
import { NodeMaterialDto, useMultiScenarioNodeData } from "@api/models";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { QueryClient } from "@tanstack/react-query";
import { mergeMaterials } from "@utils/materialUtils";
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface UseModelsDataParams {
  /** Grid API ref for the TreeGridProvider */
  assignGridApiRef: MutableRefObject<GridApiPremium>;
  /** Selected model IDs for data fetching */
  selectedModelIds: string[];
  /** Current scenario ID */
  scenarioId: string | undefined;
  /** React Query client for cache updates */
  queryClient: QueryClient;
}

export interface UseModelsDataResult {
  /** Centralized data service instance */
  modelsDataService: ModelsDataService;
  /** LCAoM service for material calculations */
  lcaoMService: LCAoMService;
  /** Node material lookup map derived from modelsDataService */
  nodeMaterialLookup: NodeMaterialLookup;
  /** Supplemental lookup map derived from modelsDataService */
  nodeSupplementalLookup: Map<number, NodeSupplementalDto>;
  /** CO2 material type lookup by ID */
  co2MaterialTypeLookup: Record<string, Co2MaterialType>;
  /** Service version for React reactivity */
  serviceVersion: number;
  /** Setter for service version to trigger re-renders */
  setServiceVersion: (version: number) => void;
  /** Updates IndexedDB with changed materials after server save */
  updateIndexedDBMaterials: (currentScenarioId: string) => Promise<void>;
  /** Multi-scenario node data query result */
  multiScenarioNodeData: ReturnType<typeof useMultiScenarioNodeData>;
  /** CO2 material types query result (scoped to the IDs actually assigned in the current scenario) */
  co2MaterialTypes: ReturnType<typeof useCo2MaterialTypesByIds>;
  /** True while assigned EC material details are still being resolved for grid display */
  isCo2MaterialTypesLoading: boolean;
  /** True when the current scenario has assigned material type IDs that need metadata lookup */
  hasAssignedMaterialTypes: boolean;
  /** True while assigned material IDs are still being discovered from cache or server */
  isDiscoveringAssignedMaterialTypes: boolean;
  /** Add a dialog-selected material to the lookup so the grid can render it before nodeMaterials refetch propagates */
  addToCo2MaterialTypeLookup: (materialType: Co2MaterialType) => void;
}

/**
 * Hook for managing model data through ModelsDataService.
 * Centralizes data fetching, hydration, and IndexedDB synchronization.
 */
export function useModelsData({
  assignGridApiRef,
  selectedModelIds,
  scenarioId,
  queryClient,
}: UseModelsDataParams): UseModelsDataResult {
  // Models data service for centralized data management
  const modelsDataService = useMemo(() => new ModelsDataService(), []);

  // LCAoM Service for client-side material calculations
  const gridProvider = useMemo(() => new GridProvider(assignGridApiRef), [assignGridApiRef]);
  const lcaoMService = useMemo(
    () => new LCAoMService(gridProvider, modelsDataService),
    [gridProvider, modelsDataService]
  );

  // Multi-model data hook - fetch nodes, materials, and supplementals for selected models
  // Data is stored in IndexedDB for TreeBuilderWorker access
  const multiScenarioNodeData = useMultiScenarioNodeData(selectedModelIds, scenarioId, {
    enabled: selectedModelIds.length > 0,
  });

  // Only fetch emission factors referenced by assigned grid rows (never the full catalog).
  // Merge IDB-cached IDs (available immediately) with live nodeMaterialsMap IDs (after server fetch).
  const { ids: cachedAssignedMaterialTypeIds, isLoading: isCachedIdsLoading } = useCachedAssignedCo2MaterialTypeIds(
    selectedModelIds,
    scenarioId
  );
  const liveAssignedMaterialTypeIds = useAssignedCo2MaterialTypeIds(multiScenarioNodeData.nodeMaterialsMap);
  const assignedMaterialTypeIds = useMemo(() => {
    const ids = new Set<string>([...cachedAssignedMaterialTypeIds, ...liveAssignedMaterialTypeIds]);
    return Array.from(ids);
  }, [cachedAssignedMaterialTypeIds, liveAssignedMaterialTypeIds]);

  const hasAssignedMaterialTypes = assignedMaterialTypeIds.length > 0;

  const dataContextKey = `${scenarioId ?? ""}:${selectedModelIds.join(",")}`;

  // Background fetch for grid display columns only — does not gate assign/estimate buttons.
  const co2MaterialTypes = useCo2MaterialTypesByIds(assignedMaterialTypeIds, {
    enabled: hasAssignedMaterialTypes,
    scenarioId,
  });

  // Holds materials that the user just selected through the assign dialog. These rows
  // need to be in the lookup immediately so the grid can render them on the next
  // re-render, before nodeMaterials refetches and the by-ids query picks them up.
  const [sessionMaterials, setSessionMaterials] = useState<Map<string, Co2MaterialType>>(() => new Map());

  // Track what we've already hydrated to prevent duplicate hydration
  const lastHydratedRef = useRef<{
    scenarioId: string | null;
    nodeMaterialsCount: number;
    nodeSupplementalsCount: number;
  } | null>(null);

  // Scenario/model changes must drop dialog-session cache and allow fresh hydration.
  useEffect(() => {
    setSessionMaterials(new Map());
    lastHydratedRef.current = null;
  }, [dataContextKey]);

  const addToCo2MaterialTypeLookup = useCallback((materialType: Co2MaterialType) => {
    const id = materialType.co2MaterialTypeId;
    if (!id) return;
    setSessionMaterials((prev) => {
      if (prev.get(id) === materialType) return prev;
      const next = new Map(prev);
      next.set(id, materialType);
      return next;
    });
  }, []);

  // Track service version to trigger re-renders on material changes
  const [serviceVersion, setServiceVersion] = useState(0);

  // Hydrate ModelsDataService with existing materials and supplementals from the database
  useEffect(() => {
    if (!multiScenarioNodeData.isSuccess || !scenarioId) return;

    // Skip re-hydration if we have local changes that haven't been persisted
    if (modelsDataService.hasLocalChanges()) {
      console.log("[useModelsData] Skipping hydration - local changes exist");
      return;
    }

    const nodeMaterialsMap = multiScenarioNodeData.nodeMaterialsMap;
    const nodeSupplementalsMap = multiScenarioNodeData.nodeSupplementalsMap;

    // Skip if we've already hydrated with the same data
    const currentSignature = {
      scenarioId,
      nodeMaterialsCount: nodeMaterialsMap.size,
      nodeSupplementalsCount: nodeSupplementalsMap.size,
    };
    if (
      lastHydratedRef.current &&
      lastHydratedRef.current.scenarioId === currentSignature.scenarioId &&
      lastHydratedRef.current.nodeMaterialsCount === currentSignature.nodeMaterialsCount &&
      lastHydratedRef.current.nodeSupplementalsCount === currentSignature.nodeSupplementalsCount
    ) {
      console.log("[useModelsData] Skipping hydration - already hydrated with same data");
      return;
    }

    console.log(
      `[useModelsData] Hydrating ModelsDataService with ${nodeMaterialsMap.size} materials and ${nodeSupplementalsMap.size} supplementals`
    );

    // Hydrate nodeSupplementals (always update, even if empty)
    modelsDataService.setNodeSupplementals(nodeSupplementalsMap);

    // Hydrate nodeMaterials (always update, even if empty - this handles removal scenarios)
    modelsDataService.loadOriginalNodeMaterials(nodeMaterialsMap, scenarioId);
    lcaoMService.clearHistory();

    // Update service version to trigger grid re-renders with new data
    setServiceVersion(modelsDataService.version);

    lastHydratedRef.current = currentSignature;
  }, [
    multiScenarioNodeData.isSuccess,
    multiScenarioNodeData.nodeMaterialsMap,
    multiScenarioNodeData.nodeSupplementalsMap,
    modelsDataService,
    lcaoMService,
    scenarioId,
    setServiceVersion,
  ]);

  // Derive node material lookup from data service - auto-updates when version changes
  const nodeMaterialLookup = useMemo(
    () => modelsDataService.nodeMaterials,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceVersion, modelsDataService]
  );

  // Derive CO2 material type lookup from the by-ids query plus any dialog-selected
  // materials added during this session (so newly-assigned rows render immediately).
  const co2MaterialTypeLookup = useMemo(() => {
    const map: Record<string, Co2MaterialType> = {};
    (co2MaterialTypes.data ?? []).forEach((materialType) => {
      const id = normalizeCo2MaterialTypeId(materialType.co2MaterialTypeId);
      if (id) {
        map[id] = materialType;
      }
    });
    sessionMaterials.forEach((materialType, id) => {
      const key = normalizeCo2MaterialTypeId(id);
      if (key && !(key in map)) {
        map[key] = materialType;
      }
    });
    return map;
  }, [co2MaterialTypes.data, sessionMaterials]);

  const resolvedCo2MaterialTypeIds = useMemo(() => {
    const resolved = new Set<string>();
    sessionMaterials.forEach((_, id) => {
      const normalizedId = normalizeCo2MaterialTypeId(id);
      if (normalizedId) resolved.add(normalizedId);
    });
    (co2MaterialTypes.data ?? []).forEach((materialType) => {
      const id = normalizeCo2MaterialTypeId(materialType.co2MaterialTypeId);
      if (id) {
        resolved.add(id);
      }
    });
    return resolved;
  }, [co2MaterialTypes.data, sessionMaterials]);

  const unresolvedCo2MaterialTypeIds = useMemo(
    () =>
      assignedMaterialTypeIds.filter((id) => {
        const normalizedId = normalizeCo2MaterialTypeId(id);
        return normalizedId != null && !resolvedCo2MaterialTypeIds.has(normalizedId);
      }),
    [assignedMaterialTypeIds, resolvedCo2MaterialTypeIds]
  );

  const isDiscoveringAssignedIds =
    isCachedIdsLoading || multiScenarioNodeData.isLoading || multiScenarioNodeData.isFetching;

  // Keep the grid overlay visible until every assigned co2MaterialTypeId can be resolved
  // into EC Material / Factor / Unit columns. Session materials (from the assign dialog)
  // count as resolved so a just-assigned row does not flash a spinner.
  const isCo2MaterialTypesLoading = useMemo(() => {
    if (!scenarioId || selectedModelIds.length === 0) return false;

    if (unresolvedCo2MaterialTypeIds.length > 0) {
      if (co2MaterialTypes.isFetching || co2MaterialTypes.isLoading) return true;
      // Query has not run yet for the current ID set (e.g. IDs just arrived from server).
      if (!co2MaterialTypes.isFetched) return true;
      return false;
    }

    // Assigned IDs may still be discovered from IDB cache or the scenario-node-data fetch.
    if (isDiscoveringAssignedIds) {
      if (isCachedIdsLoading) return true;
      if (hasAssignedMaterialTypes) return true;
    }

    return false;
  }, [
    co2MaterialTypes.isFetched,
    co2MaterialTypes.isFetching,
    co2MaterialTypes.isLoading,
    hasAssignedMaterialTypes,
    isCachedIdsLoading,
    isDiscoveringAssignedIds,
    scenarioId,
    selectedModelIds.length,
    unresolvedCo2MaterialTypeIds.length,
  ]);

  // Derive supplementalLookup from data service
  const nodeSupplementalLookup = modelsDataService.nodeSupplementals;

  /**
   * Updates IndexedDB with changed materials after successful server save.
   * This implements write-through caching so TreeBuilderWorker has access to latest data.
   */
  const updateIndexedDBMaterials = useCallback(
    async (currentScenarioId: string) => {
      console.log(
        `[useModelsData:updateIDB] Starting update for scenario: ${currentScenarioId}, models: ${selectedModelIds.join(", ")}`
      );
      console.log(`[useModelsData:updateIDB] lcaoMService.hasLocalChanges: ${lcaoMService.hasLocalChanges()}`);

      const changedMaterialsByModel = lcaoMService.getChangedMaterialsByModel(currentScenarioId, selectedModelIds);
      console.log(
        `[useModelsData:updateIDB] changedMaterialsByModel:`,
        Object.entries(changedMaterialsByModel).map(([k, v]) => `${k}: ${v.length}`)
      );

      if (Object.keys(changedMaterialsByModel).length === 0) {
        console.warn(`[useModelsData:updateIDB] No changed materials found! Checking service state...`);
        const changes = lcaoMService.getChanges(currentScenarioId);
        console.log(`[useModelsData:updateIDB] getChanges result:`, {
          added: changes.added.length,
          updated: changes.updated.length,
          removed: changes.removed.length,
        });
      }

      for (const [modelId, changedMaterials] of Object.entries(changedMaterialsByModel)) {
        if (changedMaterials.length === 0) continue;

        try {
          // Read existing materials from IDB
          // Handle both old format (NodeMaterialDto[]) and new format ([nodeId, NodeMaterialDto][])
          const existingRaw =
            (await readModelData<[number, NodeMaterialDto][] | NodeMaterialDto[]>("scenarioNodeMaterials", modelId, currentScenarioId)) || [];
          
          // Convert to array format for merging
          let existing: NodeMaterialDto[];
          if (existingRaw.length > 0 && Array.isArray(existingRaw[0])) {
            // New format: [nodeId, NodeMaterialDto][] - extract DTOs
            existing = (existingRaw as [number, NodeMaterialDto][]).map(([, dto]) => dto);
          } else {
            // Old format: NodeMaterialDto[] - use directly
            existing = existingRaw as NodeMaterialDto[];
          }
          console.log(`[useModelsData:updateIDB] Existing materials for ${modelId}: ${existing.length}`);

          // Merge changes (upsert by nodeId)
          const updated = mergeMaterials(existing, changedMaterials);
          console.log(
            `[useModelsData:updateIDB] After merge for ${modelId}: ${updated.length} (added ${changedMaterials.length})`
          );

          // Convert back to serialized Map format for IDB storage
          const updatedSerialized = serializeMap(materialsArrayToMap(updated));

          // Write back to IDB
          await writeModelData("scenarioNodeMaterials", modelId, currentScenarioId, updatedSerialized);

          // Also update React Query caches so other components see the change
          // Update individual materials cache
          queryClient.setQueryData(["scenarioNodeMaterials", modelId, currentScenarioId], updated);

          // Update the batch scenarioNodeData cache (used by MultiModelDataTest)
          queryClient.setQueryData(["scenarioNodeData", modelId, currentScenarioId], (oldData: unknown) => {
            if (!oldData) return oldData;
            // Update nodeMaterials in the ScenarioNodeDataDto
            return { ...(oldData as object), nodeMaterials: updated };
          });

          console.log(
            `[useModelsData] Updated IDB + React Query for model ${modelId}: ${changedMaterials.length} materials merged`
          );
        } catch (err) {
          console.warn(`[useModelsData] Error updating IDB for model ${modelId}:`, err);
        }
      }
    },
    [lcaoMService, selectedModelIds, queryClient]
  );

  return {
    modelsDataService,
    lcaoMService,
    nodeMaterialLookup,
    nodeSupplementalLookup,
    co2MaterialTypeLookup,
    serviceVersion,
    setServiceVersion,
    updateIndexedDBMaterials,
    multiScenarioNodeData,
    co2MaterialTypes,
    isCo2MaterialTypesLoading,
    hasAssignedMaterialTypes,
    isDiscoveringAssignedMaterialTypes: isDiscoveringAssignedIds,
    addToCo2MaterialTypeLookup,
  };
}
